// Utility helper functions
class Helpers {
    /**
     * Format currency values
     * @param {number} value - Numeric value to format
     * @param {number} decimals - Number of decimal places
     * @returns {string} - Formatted currency string
     */
    formatCurrency(value, decimals = 2) {
        if (value === null || value === undefined || isNaN(value)) {
            return '-';
        }
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
    }

    /**
     * Format percentage values
     * @param {number} value - Numeric value to format as percentage
     * @param {number} decimals - Number of decimal places
     * @returns {string} - Formatted percentage string
     */
    formatPercentage(value, decimals = 1) {
        if (value === null || value === undefined || isNaN(value)) {
            return '-';
        }
        return `${(value * 100).toFixed(decimals)}%`;
    }

    /**
     * Format date/time values
     * @param {string|Date} date - Date to format
     * @param {boolean} includeTime - Whether to include time
     * @returns {string} - Formatted date string
     */
    formatDate(date, includeTime = true) {
        if (!date) return '-';
        
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(dateObj.getTime())) return '-';
        
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        };
        
        if (includeTime) {
            options.hour = '2-digit';
            options.minute = '2-digit';
        }
        
        return dateObj.toLocaleDateString('en-US', options);
    }

    /**
     * Format time ago (relative time)
     * @param {string|Date} date - Date to format
     * @returns {string} - Relative time string
     */
    formatTimeAgo(date) {
        if (!date) return '-';
        
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(dateObj.getTime())) return '-';
        
        const now = new Date();
        const diffMs = now - dateObj;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return this.formatDate(dateObj, false);
    }

    /**
     * Debounce function calls
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} - Debounced function
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Throttle function calls
     * @param {Function} func - Function to throttle
     * @param {number} limit - Time limit in milliseconds
     * @returns {Function} - Throttled function
     */
    throttle(func, limit) {
        let inThrottle;
        return function(...args) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    /**
     * Deep clone an object
     * @param {Object} obj - Object to clone
     * @returns {Object} - Cloned object
     */
    deepClone(obj) {
        if (obj === null || typeof obj !== 'object') return obj;
        if (obj instanceof Date) return new Date(obj.getTime());
        if (obj instanceof Array) return obj.map(item => this.deepClone(item));
        if (typeof obj === 'object') {
            const cloned = {};
            Object.keys(obj).forEach(key => {
                cloned[key] = this.deepClone(obj[key]);
            });
            return cloned;
        }
    }

    /**
     * Generate CSV from array of objects
     * @param {Array} data - Array of objects to convert
     * @param {Array} headers - Optional header mapping
     * @returns {string} - CSV string
     */
    generateCSV(data, headers = null) {
        if (!data || data.length === 0) return '';
        
        const keys = headers || Object.keys(data[0]);
        const csvHeaders = keys.join(',');
        
        const csvRows = data.map(row => {
            return keys.map(key => {
                const value = row[key];
                if (value === null || value === undefined) return '';
                if (typeof value === 'string' && value.includes(',')) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
            }).join(',');
        });
        
        return [csvHeaders, ...csvRows].join('\n');
    }

    /**
     * Download CSV file
     * @param {string} csvContent - CSV content
     * @param {string} filename - Filename for download
     */
    downloadCSV(csvContent, filename = 'export.csv') {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }

    /**
     * Show error message with security-aware handling
     * @param {string} message - Error message to display
     * @param {number} duration - Duration in milliseconds
     * @param {string} type - Error type (security, network, auth)
     */
    showError(message, duration = 5000, type = 'general') {
        const container = document.getElementById('error-container');
        if (!container) return;
        
        const errorDiv = document.createElement('div');
        errorDiv.className = `error-message error-${type}`;
        
        // Add appropriate icon based on error type
        const icon = this.getErrorIcon(type);
        errorDiv.innerHTML = `${icon} ${this.sanitizeHTML(message)}`;
        
        // Adjust duration based on error severity
        if (type === 'security' || type === 'auth') {
            duration = Math.max(duration, 10000); // At least 10 seconds for security errors
        }
        
        container.appendChild(errorDiv);
        
        setTimeout(() => {
            if (errorDiv.parentNode) {
                errorDiv.parentNode.removeChild(errorDiv);
            }
        }, duration);
        
        // Log security errors for debugging
        if (type === 'security' || type === 'auth') {
            console.warn(`Security Error [${type}]:`, message);
        }
    }
    
    /**
     * Get error icon based on type
     * @param {string} type - Error type
     * @returns {string} - Icon HTML
     */
    getErrorIcon(type) {
        const icons = {
            security: '🔒',
            auth: '🔐',
            network: '🌐',
            rate_limit: '⏰',
            connection: '🔌',
            general: '⚠️'
        };
        return icons[type] || icons.general;
    }

    /**
     * Show loading state
     * @param {boolean} show - Whether to show or hide loading
     * @param {string} message - Optional loading message
     */
    showLoading(show, message = 'Loading...') {
        const overlay = document.getElementById('loading-overlay');
        if (!overlay) return;
        
        if (show) {
            const textElement = overlay.querySelector('.loading-text');
            if (textElement && message) textElement.textContent = message;
            overlay.style.display = 'flex';  // Override inline display: none
            overlay.classList.remove('hidden');
            overlay.classList.add('show');
        } else {
            overlay.classList.remove('show');
            overlay.classList.add('hidden');
            overlay.style.display = 'none';
        }
    }

    /**
     * Sanitize HTML to prevent XSS
     * @param {string} str - String to sanitize
     * @returns {string} - Sanitized string
     */
    sanitizeHTML(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    /**
     * Get confidence color based on value
     * @param {number} confidence - Confidence value (0-1)
     * @returns {string} - CSS color value
     */
    getConfidenceColor(confidence) {
        if (confidence >= 0.8) return '#3fb950';
        if (confidence >= 0.6) return '#d29922';
        return '#f85149';
    }
    
    /**
     * Check if error message indicates rate limiting
     * @param {string} message - Error message
     * @returns {boolean} - True if rate limited
     */
    isRateLimitError(message) {
        if (!message) return false;
        const msg = message.toLowerCase();
        return CONFIG.SECURITY.RATE_LIMIT_MESSAGES.some(pattern => 
            msg.includes(pattern.toLowerCase())
        );
    }
    
    /**
     * Check if error message indicates connection limit
     * @param {string} message - Error message
     * @returns {boolean} - True if connection limited
     */
    isConnectionLimitError(message) {
        if (!message) return false;
        const msg = message.toLowerCase();
        return CONFIG.SECURITY.CONNECTION_LIMIT_MESSAGES.some(pattern => 
            msg.includes(pattern.toLowerCase())
        );
    }
    
    /**
     * Get appropriate error type from message
     * @param {string} message - Error message
     * @param {number} statusCode - HTTP status code
     * @returns {string} - Error type
     */
    getErrorType(message, statusCode = null) {
        if (statusCode === 429 || this.isRateLimitError(message)) {
            return 'rate_limit';
        }
        if (statusCode === 401 || message?.toLowerCase().includes('auth')) {
            return 'auth';
        }
        if (this.isConnectionLimitError(message)) {
            return 'connection';
        }
        if (message?.toLowerCase().includes('network') || message?.toLowerCase().includes('fetch')) {
            return 'network';
        }
        if (statusCode === 429 || message?.toLowerCase().includes('security')) {
            return 'security';
        }
        return 'general';
    }

    /**
     * Get direction badge class
     * @param {string} direction - Trade direction
     * @returns {string} - CSS class name
     */
    getDirectionClass(direction) {
        return direction && direction.toLowerCase() === 'long' ? 'direction-long' : 'direction-short';
    }
}

// Export for ES6 modules
export { Helpers };

// Export singleton instance
window.Helpers = new Helpers();
